/**
 * Speedtest UI types — the canonical home for metric/phase identity.
 *
 * `MetricId` (4-set: ping, jitter, download, upload) is what the UI shows;
 * `PhaseId` (3-set: ping, download, upload) is what the engine drives. Jitter
 * rides the ping phase — `phaseFor("jitter") === "ping"` — every other
 * metric maps to itself.
 *
 * `METRIC_DEFINITIONS` is the single source of truth for label, abbreviation,
 * default unit, color, and description. Every consumer (chart, pill, results,
 * formatter, color) reads through this Record. The legacy four parallel
 * registries (chartMetrics, MetricPillCluster.metricDefs,
 * SpeedtestResults.metrics, useSpeedtestStore.formatUnit) collapse to views
 * over it.
 *
 * The engine `Phase` type lives at `@speedtest/types.ts` for the
 * worker-wire format; the two are intentionally distinct vocabulary.
 *
 * `TestState` re-exports directly from `@features/speedtest/state/types.ts`
 * (its canonical home, a leaf in the dep graph) to avoid the cycle:
 * types/speedtest → state/index → engine → metric-state →
 * types/speedtest. The deep leaf path (not the `@features/speedtest`
 * barrel) is load-bearing: routing through the barrel would re-introduce
 * exactly that cycle. Consumers still import from `@src/types/speedtest.ts`.
 */

export { TestState } from "@features/speedtest/state/types.ts";
export type {
    LiveTestData,
    MetricStates,
    UnitInfo,
    PhaseCompletePayload,
    SpeedtestStateAmountKind,
} from "@features/speedtest/state/types.ts";

// ── Metric + phase identity ──────────────────────────────────────────

/** Stable identifier for the four UI-visible metrics. */
export type MetricId = "ping" | "jitter" | "download" | "upload";

/** Stable identifier for the three engine phases.
 *
 * Aliased to the engine's `Phase` so the UI and the worker speak one
 * vocabulary — adding a future phase happens in one place
 * (`@speedtest/types.ts`) and every consumer reads through. */
export type PhaseId = import("@speedtest/types.ts").Phase;

export const METRIC_IDS: readonly MetricId[] = [
    "ping",
    "jitter",
    "download",
    "upload",
] as const;

export const PHASE_IDS: readonly PhaseId[] = [
    "ping",
    "download",
    "upload",
] as const;

const METRIC_ID_SET: ReadonlySet<string> = new Set(METRIC_IDS);
const PHASE_ID_SET: ReadonlySet<string> = new Set(PHASE_IDS);

export function isMetricId(value: string): value is MetricId {
    return METRIC_ID_SET.has(value);
}

export function isPhaseId(value: string): value is PhaseId {
    return PHASE_ID_SET.has(value);
}

/** Map a UI metric to its driving engine phase. Jitter rides ping; the
 *  other three metrics are 1:1 with their phases. */
export function phaseFor(metric: MetricId): PhaseId {
    return metric === "jitter" ? "ping" : metric;
}

/** Whether a metric reads as latency (ms). Both ping and jitter qualify. */
export function isLatencyMetric(value: string): boolean {
    return value === "ping" || value === "jitter";
}

// ── Metric definitions registry ──────────────────────────────────────

/** Lucide icon name (kebab-case). Narrow string-literal union — one entry
 *  per icon the speedtest metric set actually needs. Keeping the union
 *  narrow (rather than `type LucideIconName = string`) gives compile-time
 *  typo protection at every consumer that resolves `metric.icon` through
 *  `getLucideIcon()`. Adding a new metric icon: extend this union AND
 *  the `ICONS` map at `src/utils/icons.ts` in the same edit. */
export type LucideIconName =
    | "arrow-down"
    | "arrow-up"
    | "activity"
    | "wifi";

/** Metric descriptor — every consumer reads label, abbreviation, default
 *  unit, colors, description, and icon from a single Record keyed by
 *  `MetricId`.
 *
 *  Three color forms travel together. `color` is the canvas-safe hex
 *  (echarts, Canvas2D, anywhere `var()` can't resolve); `colorVar` is the
 *  CSS-token reference for stylesheet contexts that paint the saturated
 *  brand register (rings, fills, gradients). `colorVarLabel` is the WCAG
 *  companion token reference — same hue rotation, luminance dropped for
 *  4.5:1 body contrast, used by text-on-background callsites.
 *
 *  The PHASE COLOR CANON lives at the speedtest project's `styles/
 *  tokens.css` as four `--phase-{ping,jitter,download,upload}` hex
 *  bases. `color` below MUST mirror those four hex values verbatim —
 *  the two literal sets are bound by contract (CSS and TS cannot share
 *  a literal). `colorVar` / `colorVarLabel` reference the `--chart-*` /
 *  `--chart-*-label` tokens, both of which DERIVE from the same four
 *  bases. The canvas meter's ring ladder also derives from `color`
 *  (see `meter/rings.ts`), so a phase's dial rings, chart hue, and
 *  WCAG label can never drift apart. */
export interface MetricDef {
    id: MetricId;
    /** Speedtest-facing label ("Latency" rather than "Ping"). */
    label: string;
    /** Dashboard-facing label ("Ping" rather than "Latency"). */
    chartLabel: string;
    /** Three-letter abbreviation (LAT/JIT/DL/UL). */
    abbr: string;
    /** Slug for ARIA / DOM keys. */
    ariaLabel: string;
    /** Default display unit (ms for latency, Mbps for throughput). */
    defaultUnit: string;
    /** Canvas-safe hex color (Canvas2D + echarts can't resolve var()). */
    color: `#${string}`;
    /** CSS-token reference (`var(--chart-*)`) for stylesheet consumers
     *  painting the saturated canvas register (rings, fills, gradients). */
    colorVar: string;
    /** WCAG companion token reference (`var(--chart-*-label)`).
     *  Body-text callsites read this so the label clears 4.5:1 contrast
     *  on `--background`. Canvas / fill / gradient consumers continue
     *  to read `colorVar`. */
    colorVarLabel: string;
    /** Whether this metric's amount is a latency reading (ms). */
    isLatency: boolean;
    /** Popover/tooltip description. */
    description: string;
    /** Lucide icon name (kebab-case). Resolves to a Vue Component via
     *  `getLucideIcon()` at `@src/utils/icons.ts`. */
    icon: LucideIconName;
}

export const METRIC_DEFINITIONS: Readonly<Record<MetricId, MetricDef>> = {
    ping: {
        id: "ping",
        label: "Latency",
        chartLabel: "Ping",
        abbr: "LAT",
        ariaLabel: "latency",
        defaultUnit: "ms",
        // Mirrors `--phase-ping` in styles/tokens.css (canon). AN D3 lift: #3B6FC8 → #5B8DEF.
        color: "#5B8DEF",
        colorVar: "var(--chart-ping)",
        colorVarLabel: "var(--chart-ping-label)",
        isLatency: true,
        description: "Network latency — lower is better",
        icon: "wifi",
    },
    jitter: {
        id: "jitter",
        label: "Jitter",
        chartLabel: "Jitter",
        abbr: "JIT",
        ariaLabel: "jitter",
        defaultUnit: "ms",
        // Mirrors `--phase-jitter` in styles/tokens.css (canon).
        color: "#9B59B6",
        colorVar: "var(--chart-jitter)",
        colorVarLabel: "var(--chart-jitter-label)",
        isLatency: true,
        description: "Latency consistency — lower is better",
        icon: "activity",
    },
    download: {
        id: "download",
        label: "Download",
        chartLabel: "Download",
        abbr: "DL",
        ariaLabel: "download-speed",
        defaultUnit: "Mbps",
        // Mirrors `--phase-download` in styles/tokens.css (canon). AN D3 lift: #C81F2E → #CC2233.
        color: "#CC2233",
        colorVar: "var(--chart-download)",
        colorVarLabel: "var(--chart-download-label)",
        isLatency: false,
        description: "Download throughput",
        icon: "arrow-down",
    },
    upload: {
        id: "upload",
        label: "Upload",
        chartLabel: "Upload",
        abbr: "UL",
        ariaLabel: "upload-speed",
        defaultUnit: "Mbps",
        // Mirrors `--phase-upload` in styles/tokens.css (canon). AN D3 lift: #D47A1A → #E09030.
        color: "#E09030",
        colorVar: "var(--chart-upload)",
        colorVarLabel: "var(--chart-upload-label)",
        isLatency: false,
        description: "Upload throughput",
        icon: "arrow-up",
    },
};

// ── Canonical formatters ──────────────────────────────────────────────
//
// `formatMetricValue` is the single rounding policy for the dashboard.
// Prior to AC.W8c each surface carried its own `fmt()` / `formatValue()`
// helper (ResultsTable integer-rounded; ResultDetailSheet two-decimal-
// rounded; MapTooltip switched at 100; DistributionChart appended unit
// to summary but stripped it on markLine names). The same 87.3 Mbps
// value rendered as 4 different strings depending on which surface
// the user landed on.
//
// One canon, one shape: integer for ≥ 100; one-decimal for < 100.
// The latency / throughput distinction lives in the call-site (each
// surface knows the metric it's rendering); this function is the
// shared rounding contract.
//
// Variants:
//   - `format(value, unit?)` — main entry; `null`/`undefined` → em-dash;
//     emits `"87 Mbps"` / `"23.4 ms"` / `"23.4"` (no unit) shapes.
//   - `formatBare(value)` — same rounding without the em-dash branch
//     (for chart markLine names that already gate on numeric inputs).

const EM_DASH = "—";

/**
 * Format a metric value with the dashboard's canonical rounding policy.
 *
 * Returns an em-dash for null/undefined/NaN. Otherwise:
 *   - `v >= 100` → integer (`87`)
 *   - `v < 100`  → one decimal (`87.3`)
 *
 * If `unit` is provided, the unit appends with a single space.
 */
export function formatMetricValue(
    value: number | null | undefined,
    unit = "",
): string {
    if (value == null || !Number.isFinite(value)) return EM_DASH;
    const rounded = value >= 100 ? Math.round(value).toString() : value.toFixed(1);
    return unit ? `${rounded} ${unit}` : rounded;
}

/**
 * Bare numeric formatter — same rounding as `formatMetricValue` minus
 * the null branch. Suitable for chart markLine names where the input
 * is guaranteed numeric.
 */
export function formatMetricBare(value: number): string {
    return value >= 100 ? Math.round(value).toString() : value.toFixed(1);
}
